import React, { useState } from 'react';
import { Helmet } from 'react-helmet';
import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { useAuth } from '@/contexts/FirebaseAuthContext';
import { useToast } from '@/components/ui/use-toast';
import { ArrowLeft, Mic, Users, Tv, Smartphone, QrCode, Star, CheckCircle, Check, X } from 'lucide-react';
import { checkSlugAvailability, generateUrlSlug } from '@/lib/utils';
import { isQrPromoReservedSlug, QR_SLUG_RESERVED_MESSAGE } from '@/lib/reservedUrlSlug';
import { config } from '@/lib/config';

const KaraokeSignupPage = () => {
  const { signUp } = useAuth();
  const { toast } = useToast();
  const [formData, setFormData] = useState({
    fullName: '',
    artist_name: '',
    url_slug: '',
    email: '',
    password: '',
    confirmPassword: '',
    dateOfBirth: ''
  });
  const [acceptedPrivacyPolicy, setAcceptedPrivacyPolicy] = useState(false);
  const [acceptedTerms, setAcceptedTerms] = useState(false);
  const [loading, setLoading] = useState(false);
  const [checkingSlug, setCheckingSlug] = useState(false);
  const [slugAvailable, setSlugAvailable] = useState(null);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
    
    // Auto-generate URL slug when artist name changes
    if (name === 'artist_name') {
      const generatedSlug = generateUrlSlug(value);
      setFormData(prev => ({ ...prev, url_slug: generatedSlug }));
      checkSlugAvailabilityRealTime(generatedSlug);
    }
    
    // Check URL slug availability when it changes
    if (name === 'url_slug') {
      checkSlugAvailabilityRealTime(value);
    }
  };

  const checkSlugAvailabilityRealTime = async (slug) => {
    if (!slug || slug.length < 3) {
      setSlugAvailable(null);
      return;
    }
    if (isQrPromoReservedSlug(slug)) {
      setCheckingSlug(false);
      setSlugAvailable(false);
      return;
    }

    setCheckingSlug(true);
    try {
      const isAvailable = await checkSlugAvailability(slug);
      setSlugAvailable(isAvailable);
    } catch (error) {
      console.error('Error checking slug availability:', error);
      setSlugAvailable(null);
    } finally {
      setCheckingSlug(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (formData.password !== formData.confirmPassword) {
      toast({ title: "Error", description: "Passwords don't match.", variant: "destructive" });
      return;
    }

    if (formData.password.length < 6) {
      toast({ title: "Error", description: "Password must be at least 6 characters.", variant: "destructive" });
      return;
    }

    if (!formData.url_slug.trim()) {
      toast({ title: "Error", description: "Please enter a URL slug for your profile.", variant: "destructive" });
      return;
    }

    if (isQrPromoReservedSlug(formData.url_slug.trim())) {
      toast({ title: "Error", description: QR_SLUG_RESERVED_MESSAGE, variant: "destructive" });
      return;
    }

    if (slugAvailable === false) {
      toast({ title: "Error", description: "This URL slug is already taken. Please choose another.", variant: "destructive" });
      return;
    }

    // Validate Full Name
    if (!formData.fullName || formData.fullName.trim().length < 2) {
      toast({ title: "Error", description: "Please enter your full legal name", variant: "destructive" });
      return;
    }

    // Validate DOB format (DD/MM/YYYY)
    if (!formData.dateOfBirth) {
      toast({ title: "Error", description: "Please enter your date of birth (DD/MM/YYYY)", variant: "destructive" });
      return;
    }

    const dobPattern = /^(\d{2})\/(\d{2})\/(\d{4})$/;
    if (!dobPattern.test(formData.dateOfBirth)) {
      toast({ title: "Error", description: "Date of birth must be in DD/MM/YYYY format (e.g., 01/01/1990)", variant: "destructive" });
      return;
    }

    // Parse and validate DOB
    const [day, month, year] = formData.dateOfBirth.split('/').map(Number);
    if (day < 1 || day > 31 || month < 1 || month > 12 || year < 1900 || year > new Date().getFullYear()) {
      toast({ title: "Error", description: "Please enter a valid date of birth", variant: "destructive" });
      return;
    }

    // Validate age (must be 18+)
    const birthDate = new Date(year, month - 1, day);
    const today = new Date();
    const age = today.getFullYear() - birthDate.getFullYear();
    const monthDiff = today.getMonth() - birthDate.getMonth();
    const actualAge = monthDiff < 0 || (monthDiff === 0 && today.getDate() < birthDate.getDate()) ? age - 1 : age;

    if (actualAge < 18) {
      toast({ title: "Error", description: "You must be at least 18 years old to create an account", variant: "destructive" });
      return;
    }

    // Validate checkboxes
    if (!acceptedPrivacyPolicy) {
      toast({ title: "Error", description: "Please accept the Privacy Policy to continue", variant: "destructive" });
      return;
    }

    if (!acceptedTerms) {
      toast({ title: "Error", description: "Please accept the Terms and Conditions to continue", variant: "destructive" });
      return;
    }

    setLoading(true);
    
    try {
      const { error } = await signUp(formData.email, formData.password, {
        data: { 
          fullName: formData.fullName.trim(),
          artist_name: formData.artist_name,
          url_slug: formData.url_slug.trim(),
          dateOfBirth: formData.dateOfBirth
        }
      });

      if (error) {
        toast({ title: "Error", description: error.message, variant: "destructive" });
      } else {
        toast({ title: "Success!", description: "Karaoke Host account created! Redirecting to dashboard..." });
      }
    } catch (error) {
      toast({ title: "Error", description: "An unexpected error occurred.", variant: "destructive" });
    } finally {
      setLoading(false);
    }
  };

  const getSlugStatusIcon = () => {
    if (checkingSlug) {
      return <div className="w-4 h-4 border-2 border-purple-400 border-t-transparent rounded-full animate-spin" />;
    }
    if (slugAvailable === true) {
      return <Check className="w-4 h-4 text-green-500" />;
    }
    if (slugAvailable === false) {
      return <X className="w-4 h-4 text-red-500" />;
    }
    return null;
  };

  const getSlugStatusText = () => {
    if (checkingSlug) return "Checking availability...";
    if (formData.url_slug && formData.url_slug.length >= 3 && isQrPromoReservedSlug(formData.url_slug)) {
      return "Reserved (cannot start with qr)";
    }
    if (slugAvailable === true) return "Available";
    if (slugAvailable === false) return "Already taken or unavailable";
    if (formData.url_slug && formData.url_slug.length < 3) return "Must be at least 3 characters";
    return "";
  };

  const getSlugStatusColor = () => {
    if (checkingSlug) return "text-purple-400";
    if (slugAvailable === true) return "text-green-400";
    if (slugAvailable === false) return "text-red-400";
    return "text-purple-300";
  };

  return (
    <>
      <Helmet>
        <title>Karaoke Mode Sign Up - Your Song Request</title>
        <meta name="description" content="Perfect for DJs and Karaoke Hosts. Manage song requests, moderate content, and display live queues on venue screens." />
      </Helmet>
      
      <div className="min-h-screen bg-gradient-to-br from-purple-950 via-purple-900 to-indigo-950 text-white">
        {/* Header */}
        <header className="border-b border-purple-800/50 bg-purple-900/20 backdrop-blur-sm">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="flex items-center justify-between py-4">
              <div className="flex items-center">
                <Link to="/" className="inline-flex items-center text-purple-300 hover:text-purple-200 mr-4">
                  ← Back to Main Site
                </Link>
                <span className="text-2xl font-bold text-purple-200">🎤 Karaoke Mode</span>
              </div>
              <div className="flex items-center space-x-3">
                <Link to="/karaoke/login">
                  <Button variant="outline" className="border-purple-600 text-purple-300 hover:bg-purple-600 hover:text-white">
                    Sign In
                  </Button>
                </Link>
              </div>
            </div>
          </div>
        </header>

        <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
          <motion.div 
            initial={{ opacity: 0, y: 20 }} 
            animate={{ opacity: 1, y: 0 }} 
            transition={{ duration: 0.6 }}
            className="text-center mb-12"
          >
            <h1 className="text-4xl md:text-6xl font-bold text-purple-100 mb-6">
              🎤 Karaoke Mode
            </h1>
            <p className="text-xl text-purple-300 max-w-3xl mx-auto">
              Perfect for DJs and Karaoke Hosts. Let your audience request any song, 
              moderate requests, and display live queues on venue screens.
            </p>
          </motion.div>

          <div className="grid lg:grid-cols-2 gap-12 items-start">
            {/* Sign Up Form */}
            <motion.div
              initial={{ opacity: 0, x: -30 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.6, delay: 0.2 }}
            >
              <Card className="glass-effect border-purple-600 bg-purple-900/20">
                <CardHeader className="text-center">
                  <div className="flex justify-center mb-4">
                    <Mic className="w-16 h-16 text-purple-400" />
                  </div>
                  <CardTitle className="text-3xl font-bold text-purple-100">Create Your Account</CardTitle>
                  <CardDescription className="text-purple-300">
                    Start managing karaoke sessions in minutes
                  </CardDescription>
                </CardHeader>
                <CardContent>
                  <form onSubmit={handleSubmit} className="space-y-4">
                    <div className="space-y-2">
                      <Label htmlFor="fullName" className="text-purple-200">Full Legal Name <span className="text-red-400">*</span></Label>
                      <Input
                        id="fullName"
                        name="fullName"
                        type="text"
                        value={formData.fullName}
                        onChange={handleInputChange}
                        required
                        className="bg-purple-900/50 border-purple-600 text-white placeholder-purple-400"
                        placeholder="John Smith"
                      />
                      <p className="text-purple-300 text-xs">
                        Required for identity verification and receiving payouts
                      </p>
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="artist_name" className="text-purple-200">Karaoke Host Name</Label>
                      <Input
                        id="artist_name"
                        name="artist_name"
                        type="text"
                        value={formData.artist_name}
                        onChange={handleInputChange}
                        required
                        className="bg-purple-900/50 border-purple-600 text-white placeholder-purple-400"
                        placeholder="Your DJ/Host Name"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="url_slug" className="text-purple-200">Profile URL</Label>
                      <div className="space-y-1">
                        <Input
                          id="url_slug"
                          name="url_slug"
                          type="text"
                          value={formData.url_slug}
                          onChange={handleInputChange}
                          required
                          className="bg-purple-900/50 border-purple-600 text-white placeholder-purple-400"
                          placeholder="your-karaoke-host"
                        />
                        <div className="flex items-center gap-2 text-sm">
                          {getSlugStatusIcon()}
                          <span className={getSlugStatusColor()}>
                            {getSlugStatusText()}
                          </span>
                          {formData.url_slug && (
                            <span className="text-purple-400">
                              • {config.getBaseUrl()}/karaoke/{formData.url_slug}
                            </span>
                          )}
                        </div>
                      </div>
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="email" className="text-purple-200">Email</Label>
                      <Input
                        id="email"
                        name="email"
                        type="email"
                        value={formData.email}
                        onChange={handleInputChange}
                        required
                        className="bg-purple-900/50 border-purple-600 text-white placeholder-purple-400"
                        placeholder="your@email.com"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="password" className="text-purple-200">Password</Label>
                      <Input
                        id="password"
                        name="password"
                        type="password"
                        value={formData.password}
                        onChange={handleInputChange}
                        required
                        className="bg-purple-900/50 border-purple-600 text-white placeholder-purple-400"
                        placeholder="••••••••"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="confirmPassword" className="text-purple-200">Confirm Password</Label>
                      <Input
                        id="confirmPassword"
                        name="confirmPassword"
                        type="password"
                        value={formData.confirmPassword}
                        onChange={handleInputChange}
                        required
                        className="bg-purple-900/50 border-purple-600 text-white placeholder-purple-400"
                        placeholder="••••••••"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="dateOfBirth" className="text-purple-200">Date of Birth (DD/MM/YYYY) <span className="text-red-400">*</span></Label>
                      <Input
                        id="dateOfBirth"
                        name="dateOfBirth"
                        type="text"
                        value={formData.dateOfBirth}
                        onChange={(e) => {
                          let value = e.target.value.replace(/\D/g, ''); // Remove non-digits
                          // Format as DD/MM/YYYY
                          if (value.length > 2) value = value.slice(0, 2) + '/' + value.slice(2);
                          if (value.length > 5) value = value.slice(0, 5) + '/' + value.slice(5, 9);
                          setFormData(prev => ({ ...prev, dateOfBirth: value }));
                        }}
                        required
                        maxLength={10}
                        className="bg-purple-900/50 border-purple-600 text-white placeholder-purple-400"
                        placeholder="DD/MM/YYYY"
                      />
                      <p className="text-purple-300 text-xs">
                        You must be at least 18 years old. Required for compliance and receiving payouts. Format: DD/MM/YYYY
                      </p>
                    </div>
                    
                    {/* Privacy Policy and Terms */}
                    <div className="space-y-3 pt-2">
                      <div className="flex items-start space-x-2">
                        <input
                          type="checkbox"
                          id="privacyPolicy"
                          checked={acceptedPrivacyPolicy}
                          onChange={(e) => setAcceptedPrivacyPolicy(e.target.checked)}
                          required
                          className="mt-1 w-4 h-4 text-purple-600 bg-purple-900/50 border-purple-600 rounded focus:ring-purple-500"
                        />
                        <label htmlFor="privacyPolicy" className="text-sm text-purple-200 cursor-pointer">
                          I accept the{' '}
                          <Link to="/privacy-policy" target="_blank" className="text-purple-400 hover:text-purple-300 underline">
                            Privacy Policy
                          </Link>
                        </label>
                      </div>
                      <div className="flex items-start space-x-2">
                        <input
                          type="checkbox"
                          id="terms"
                          checked={acceptedTerms}
                          onChange={(e) => setAcceptedTerms(e.target.checked)}
                          required
                          className="mt-1 w-4 h-4 text-purple-600 bg-purple-900/50 border-purple-600 rounded focus:ring-purple-500"
                        />
                        <label htmlFor="terms" className="text-sm text-purple-200 cursor-pointer">
                          I accept the{' '}
                          <Link to="/terms-of-service" target="_blank" className="text-purple-400 hover:text-purple-300 underline">
                            Terms and Conditions
                          </Link>
                        </label>
                      </div>
                    </div>

                    <Button
                      type="submit"
                      disabled={loading || !acceptedPrivacyPolicy || !acceptedTerms}
                      className="w-full bg-purple-600 hover:bg-purple-700 text-white font-semibold py-3 text-lg disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      {loading ? 'Creating Account...' : 'Create Karaoke Account'}
                    </Button>
                  </form>
                  
                  {/* Artist Mode Link */}
                  <div className="mt-6 p-4 bg-blue-900/20 border border-blue-700/50 rounded-lg">
                    <p className="text-blue-200 text-sm mb-2">
                      🎵 Looking for Artist Mode?
                    </p>
                    <Link 
                      to="/signup" 
                      className="inline-flex items-center text-blue-300 hover:text-blue-200 font-semibold text-sm transition-colors"
                    >
                      Sign Up HERE! 🎵
                    </Link>
                    <p className="text-blue-300 text-xs mt-1">
                      Perfect for Musicians and Bands
                    </p>
                  </div>
                </CardContent>
              </Card>
            </motion.div>

            {/* Features */}
            <motion.div
              initial={{ opacity: 0, x: 30 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.6, delay: 0.4 }}
              className="space-y-6"
            >
              <div className="text-center lg:text-left">
                <h2 className="text-2xl font-bold text-purple-100 mb-6">Why Karaoke Mode?</h2>
              </div>
              
              <div className="space-y-4">
                <div className="flex items-start space-x-3">
                  <div className="w-10 h-10 bg-purple-600/20 rounded-full flex items-center justify-center flex-shrink-0">
                    <Users className="w-5 h-5 text-purple-400" />
                  </div>
                  <div>
                    <h3 className="font-semibold text-purple-100">Audience Engagement</h3>
                    <p className="text-purple-300 text-sm">Let your audience request any song, not just from a limited setlist</p>
                  </div>
                </div>
                
                <div className="flex items-start space-x-3">
                  <div className="w-10 h-10 bg-purple-600/20 rounded-full flex items-center justify-center flex-shrink-0">
                    <Tv className="w-5 h-5 text-purple-400" />
                  </div>
                  <div>
                    <h3 className="font-semibold text-purple-100">Venue Display</h3>
                    <p className="text-purple-300 text-sm">Show live queues on TV screens and projectors around your venue</p>
                  </div>
                </div>
                
                <div className="flex items-start space-x-3">
                  <div className="w-10 h-10 bg-purple-600/20 rounded-full flex items-center justify-center flex-shrink-0">
                    <Smartphone className="w-5 h-5 text-purple-400" />
                  </div>
                  <div>
                    <h3 className="font-semibold text-purple-100">Mobile QR Codes</h3>
                    <p className="text-purple-300 text-sm">Audience scans QR codes to request songs from their phones</p>
                  </div>
                </div>
                
                <div className="flex items-start space-x-3">
                  <div className="w-10 h-10 bg-purple-600/20 rounded-full flex items-center justify-center flex-shrink-0">
                    <Star className="w-5 h-5 text-purple-400" />
                  </div>
                  <div>
                    <h3 className="font-semibold text-purple-100">Skip Queue Feature</h3>
                    <p className="text-purple-300 text-sm">Premium requests that jump to the front of the queue</p>
                  </div>
                </div>
                
                <div className="flex items-start space-x-3">
                  <div className="w-10 h-10 bg-purple-600/20 rounded-full flex items-center justify-center flex-shrink-0">
                    <CheckCircle className="w-5 h-5 text-purple-400" />
                  </div>
                  <div>
                    <h3 className="font-semibold text-purple-100">Content Moderation</h3>
                    <p className="text-purple-300 text-sm">Review and approve requests before they appear in the queue</p>
                  </div>
                </div>
              </div>
            </motion.div>
          </div>
        </main>
      </div>
    </>
  );
};

export default KaraokeSignupPage;
